/**
 * /downhole/tickets — field tickets (0461, `GET /downhole/tickets`).
 *
 * NO "NEW TICKET" HERE. A ticket is made from an assembly's closed runs, on the
 * assembly's page (its Tickets panel), because a ticket covers one assembly and
 * the runs to choose are that assembly's. This list links there rather than
 * offering a create that would first have to ask which assembly.
 *
 * COUNTS ARE A TALLY OF THE LOADED ROWS, and that is honest here: the list is
 * unpaginated (the route returns every live ticket) and no endpoint returns
 * the figure. The chips filter the loaded list; they do not re-query.
 *
 * Money is per ticket, in the ticket's own currency, and a draft has none:
 * `—`, never 0 — its prices are fixed when the office approves it.
 */
import React, { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, ChevronRight, FilePen, Loader2, Send, XCircle } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDownholeTickets } from '../../hooks/useDownhole';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { formatCalendarDate, formatInstantDay } from '../../lib/calendar-date';
import { fmtCurrency } from '../../lib/display-labels';
import { dhSkin } from '../../lib/downhole-labels';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { DH_TICKET_STATUSES, isDailyTicket, type DhTicket } from '../../types/downhole';
import { DhEmpty, DhPanel } from './DownholeBits';
import { DH_TICKET_STATUS_SKIN, TicketStatusBadge, canReadDownholeTickets, ticketEmailLabel } from './ticketBits';

const isStatus = (v: string | null): v is DhTicket['status'] => !!v && (DH_TICKET_STATUSES as readonly string[]).includes(v);

/**
 * A daily ticket (scope 'job_day', api#1062) bills a day of a well job, so it
 * names the job and the day rather than an assembly. Its run_numbers are the
 * day's runs across every BHA, and run numbers are per assembly (api#1071):
 * two BHAs can each send 1, so a joined list would read "Run 1, 1". It carries
 * the API's run_count instead.
 */
function ticketSubject(t: DhTicket): string {
  if (!isDailyTicket(t)) return t.assembly_number || '—';
  const day = formatCalendarDate(t.work_date, { month: 'short', day: 'numeric' });
  return `${t.job_number || '—'} · ${day ?? '—'}`;
}

function ticketRuns(t: DhTicket): string {
  if (isDailyTicket(t)) {
    if (typeof t.run_count !== 'number') return '—';
    return `${t.run_count} ${t.run_count === 1 ? 'run' : 'runs'} that day`;
  }
  return t.run_numbers.length ? `Run ${t.run_numbers.join(', ')}` : '—';
}

export default function TicketsScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canRead = canReadDownholeTickets(user?.role);
  const tz = useTenantTimeZone();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const status = isStatus(statusParam) ? statusParam : '';
  const query = useDownholeTickets(undefined, canRead);
  const failure = queryLoadFailure(query);
  const all = useMemo(() => query.data?.data ?? [], [query.data]);
  const loaded = !!query.data;
  const count = (s: DhTicket['status']) => (loaded ? all.filter((t) => t.status === s).length : undefined);
  const tickets = status ? all.filter((t) => t.status === status) : all;
  const countsLoading = canRead && query.isLoading;

  const setStatus = (next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set('status', next);
    else p.delete('status');
    setSearchParams(p, { replace: true });
  };
  const open = (id: string) => navigate(`/downhole/tickets/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(id);
    }
  };
  // A failed or refused read has no counts: `—` would claim "none".
  const stat = (s: DhTicket['status']) => (failure && !loaded ? '—' : displayValue(count(s)));
  const chips = [
    { key: '', label: 'All', count: loaded ? all.length : undefined },
    ...DH_TICKET_STATUSES.map((s) => ({ key: s, label: dhSkin(DH_TICKET_STATUS_SKIN, s).label, count: count(s) })),
  ];

  return (
    <div data-testid="downhole-tickets-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Downhole tools"
        title="Tool Tickets"
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <FilePen className="size-4" aria-hidden="true" />, label: 'Draft', sub: 'Office to approve', tone: 'neutral', loading: countsLoading, value: stat('draft') },
          { icon: <Send className="size-4" aria-hidden="true" />, label: 'With customer', sub: 'Awaiting approval', tone: 'info', loading: countsLoading, value: stat('office_approved') },
          { icon: <CheckCircle2 className="size-4" aria-hidden="true" />, label: 'Approved', sub: 'Ready to invoice', tone: 'success', loading: countsLoading, value: stat('customer_approved') },
          { icon: <XCircle className="size-4" aria-hidden="true" />, label: 'Rejected', sub: 'Sent back', tone: 'accent', loading: countsLoading, value: stat('customer_rejected') },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {canRead && (
            <div className="scrollbar-hide flex gap-2 overflow-x-auto" role="group" aria-label="Filter by status">
              {chips.map((chip) => {
                const active = status === chip.key;
                return (
                  <button
                    key={chip.key || 'all'}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setStatus(chip.key)}
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                      active ? 'bg-fi-amber text-fi-amber-ink' : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                    }`}
                  >
                    {chip.label}
                    {loaded && <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>{displayValue(chip.count)}</span>}
                  </button>
                );
              })}
            </div>
          )}

          <DhPanel
            eyebrow="Customer sign-off"
            title="Field tickets"
            testId="downhole-ticket-register"
            footer={loaded && status && all.length > 0 ? `Showing ${tickets.length} of ${all.length} tickets` : undefined}
          >
            {!canRead ? (
              <DhEmpty>Field tickets carry prices, so only admin, office staff and dispatch can see them.</DhEmpty>
            ) : query.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading tickets" />
              </div>
            ) : failure && !loaded ? (
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="field tickets" />
            ) : tickets.length === 0 ? (
              <DhEmpty>
                {status ? 'No ticket has this status.' : 'There are no field tickets yet.'}{' '}
                {!status && (
                  <>
                    A ticket is made from an assembly&rsquo;s closed runs, on{' '}
                    <Link to="/downhole/assemblies" className="font-bold text-fieldiq-accent-signal underline">
                      its page
                    </Link>
                    .
                  </>
                )}
              </DhEmpty>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[1100px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th scope="col" className="whitespace-nowrap px-4 py-3">Ticket #</th>
                        <th scope="col" className="px-4 py-3">Status</th>
                        <th scope="col" className="px-4 py-3">Customer</th>
                        <th scope="col" className="px-4 py-3">Assembly or day · runs</th>
                        <th scope="col" className="px-4 py-3">Well / Rig</th>
                        <th scope="col" className="px-4 py-3">Email</th>
                        <th scope="col" className="px-4 py-3">Created</th>
                        <th scope="col" className="px-4 py-3 text-right">Total</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map((t) => (
                        <tr
                          key={t.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => open(t.id)}
                          onKeyDown={rowKeys(t.id)}
                          className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${
                            dhSkin(DH_TICKET_STATUS_SKIN, t.status).rail
                          }`}
                        >
                          <td className="whitespace-nowrap px-4 py-3 font-mono text-sm font-extrabold text-fieldiq-accent-signal">{t.ticket_number}</td>
                          <td className="px-4 py-3"><TicketStatusBadge status={t.status} /></td>
                          <td className="min-w-[12rem] px-4 py-3 text-sm text-fieldiq-text">{displayValue(t.customer_name)}</td>
                          <td className="px-4 py-3">
                            <p className="whitespace-nowrap font-mono text-sm text-white">{ticketSubject(t)}</p>
                            <p className="text-xs text-fieldiq-muted">{ticketRuns(t)}</p>
                          </td>
                          <td className="min-w-[12rem] px-4 py-3 text-sm text-fieldiq-text">
                            {t.well_name || t.rig_name ? `${t.well_name || '—'} / ${t.rig_name || '—'}` : '—'}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{ticketEmailLabel(t.customer_email_status)}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{formatInstantDay(t.created_at, tz) ?? '—'}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm font-extrabold text-white">{fmtCurrency(t.total_amount, t.currency)}</td>
                          <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {tickets.map((t) => (
                    <div
                      key={t.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => open(t.id)}
                      onKeyDown={rowKeys(t.id)}
                      className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-mono text-sm font-extrabold text-fieldiq-accent-signal">{t.ticket_number}</p>
                        <TicketStatusBadge status={t.status} />
                      </div>
                      <p className="mt-1 text-sm text-white">{displayValue(t.customer_name)}</p>
                      <div className="mt-2 flex items-center justify-between text-xs text-fieldiq-muted">
                        <span>
                          {ticketSubject(t)} · {ticketRuns(t)}
                        </span>
                        <span className="font-mono text-sm font-extrabold text-white">{fmtCurrency(t.total_amount, t.currency)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DhPanel>
        </div>
      </section>
    </div>
  );
}
